import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { GOAL_OPTIONS } from "../../data/content";

const inputCls =
  "w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none focus:ring-0 transition-colors duration-300 text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.15em] text-white/50 mb-2 font-display";

export default function ApplicationForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    sex: "",
    weight: "",
    height: "",
    goal: "",
    blocker: "",
    time_trying: "",
    why_now: "",
    gdpr_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age, 10),
        weight: parseFloat(form.weight),
        height: parseFloat(form.height),
      };
      const { data } = await api.post("/leads", payload);
      toast.success("Formulario enviado. Elige tu hora.");
      onSuccess(data.lead_id, form.name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al enviar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="application-form" onSubmit={submit} className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Nombre *</label>
          <input data-testid="form-name" required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Tu nombre completo" />
        </div>
        <div>
          <label className={labelCls}>Email *</label>
          <input data-testid="form-email" required type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@email.com" />
        </div>
        <div>
          <label className={labelCls}>Teléfono *</label>
          <input data-testid="form-phone" required className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="600 000 000" />
        </div>
        <div>
          <label className={labelCls}>Edad *</label>
          <input data-testid="form-age" required type="number" min="16" max="99" className={inputCls} value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="30" />
        </div>
        <div>
          <label className={labelCls}>Sexo *</label>
          <select data-testid="form-sex" required className={inputCls} value={form.sex} onChange={(e) => set("sex", e.target.value)}>
            <option value="" className="bg-black">Selecciona</option>
            <option value="hombre" className="bg-black">Hombre</option>
            <option value="mujer" className="bg-black">Mujer</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Peso actual (kg) *</label>
          <input data-testid="form-weight" required type="number" step="0.1" className={inputCls} value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="75" />
        </div>
        <div>
          <label className={labelCls}>Altura (cm) *</label>
          <input data-testid="form-height" required type="number" step="0.1" className={inputCls} value={form.height} onChange={(e) => set("height", e.target.value)} placeholder="175" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Objetivo principal *</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {GOAL_OPTIONS.map((g) => (
            <button
              type="button"
              key={g.value}
              data-testid={`form-goal-${g.value}`}
              onClick={() => set("goal", g.value)}
              className={`px-3 py-3 rounded-sm border text-xs md:text-sm font-display uppercase tracking-wide transition-all ${
                form.goal === g.value
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                  : "border-white/15 text-white/60 hover:border-white/30"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className={labelCls}>¿Qué crees que te ha hecho abandonar las veces anteriores? *</label>
          <textarea data-testid="form-blocker" required rows={2} className={inputCls} value={form.blocker} onChange={(e) => set("blocker", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>¿Cuánto tiempo llevas prometiéndote que “esta vez sí”? *</label>
          <textarea data-testid="form-time-trying" required rows={2} className={inputCls} value={form.time_trying} onChange={(e) => set("time_trying", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>¿Qué cambiaría realmente en tu vida si dentro de un año consigues el cambio físico y mental que siempre has querido? *</label>
          <textarea data-testid="form-why-now" required rows={3} className={inputCls} value={form.why_now} onChange={(e) => set("why_now", e.target.value)} placeholder="Más allá de los kilos: confianza, autoestima, energía, sentirte orgulloso, recuperar el control…" />
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          data-testid="form-gdpr"
          type="checkbox"
          required
          checked={form.gdpr_consent}
          onChange={(e) => set("gdpr_consent", e.target.checked)}
          className="mt-1 accent-[#D4AF37] w-4 h-4"
        />
        <span className="text-xs text-white/50 leading-relaxed">
          He leído y acepto la{" "}
          <a href="/privacidad" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">política de privacidad</a>{" "}
          y consiento el tratamiento de mis datos para gestionar mi solicitud (RGPD).
        </span>
      </label>

      <button
        data-testid="form-submit"
        type="submit"
        disabled={!form.goal || loading}
        className="btn-gold rounded-sm w-full py-4 text-sm md:text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <>Quiero empezar mi cambio <ArrowRight size={18} /></>}
      </button>
    </form>
  );
}
